import { Formik } from 'formik';
import React from 'react';
import { withTranslation } from 'react-i18next';
import { Form, FormGroup, Label, CustomInput } from 'reactstrap';
import Datagrid from '../../../../UI/Datagrid';
import Backend from '../../../../Layouts/Backend';
import { Alert, Button, Input } from '../../../../UI/Common';
import { DialogBox } from '../../../../UI/Modal';
import validationSchema from '../validation';
import { useHistory } from 'react-router';

function EditBatchReportPage({ t, ...props }) {
  const { batchReport = {} } = props;
  const history = useHistory();
  const groupsFormatter = (cell) => {
    return cell.map((group) => group.groupName).join(', ');
  };
  const columns = [
    { dataField: 'process', text: 'Process', sort: true },
    {
      dataField: 'groups',
      text: 'Group',
      sort: true,
      formatter: groupsFormatter,
      filterValue: groupsFormatter,
    },
    {
      dataField: 'beginTime',
      text: 'Beginning',
      sort: true,
      isDateTime: true,
      headerStyle: { width: 150 },
    },
    {
      dataField: 'endTime',
      text: 'Ending',
      isDateTime: true,
      headerStyle: { width: 150 },
      sort: true,
    },
  ];
  const defaultAddCol = {
    dataField: 'additional',
    text: 'Filler',
    sort: true,
  };
  const [addCol, setAddCol] = React.useState({
    dataField: 'additional',
    text: 'Additional Field',
    sort: true,
  });
  const [state, setState] = React.useState({
    openModal: false,
    selectedRow: null,
  });
  const defaultSorted = [{ dataField: 'process', order: 'asc' }];
  const ctxMenuOptions = [
    [
      {
        name: 'Edit process',
        action: (row) =>
          history.push(
            `/reports/batch-reports/edit-process?deptId=${batchReport.deptId}&batchId=${batchReport.id}&&processId=${row.id}`,
          ),
      },
      {
        name: 'Delete process',
        action: (row) => {
          setState({ openModal: true, selectedRow: row });
        },
      },
    ],
  ];
  React.useEffect(() => {
    if (Object.keys(batchReport).length) {
      setAddCol({
        ...addCol,
        text: batchReport.additionalField.trim(),
      });
    }
  }, [batchReport]);
  function onDeleteProcess() {
    setState({ openModal: false });
    props.onDeleteProcess(state.selectedRow.id);
  }
  function onCancel() {
    setState({ openModal: false, selectedRow: null });
  }
  if (props.getBatchReportError) {
    return (
      <Backend>
        <div className="content-inner">{props.getBatchReportError}</div>
      </Backend>
    );
  }
  return (
    <Backend title={`Edit Batch Report: ${batchReport.batchNumber}`}>
      <DialogBox
        isOpen={state.openModal}
        title="Delete batch process"
        body="Are you sure you want to delete this batch process?"
        yesBtnLabel="Delete"
        onYes={onDeleteProcess}
        noBtnLabel="Cancel"
        onCancel={onCancel}
      />
      <div className="content-inner">
        <Formik
          enableReinitialize
          initialValues={batchReport}
          validationSchema={validationSchema}
          onSubmit={(values) => props.onEditBatch(values)}
        >
          {({
            values,
            handleChange,
            errors,
            setFieldValue,
            setFieldTouched,
            touched,
            handleSubmit,
          }) => {
            return (
              <Form className="w-50">
                <Input
                  required
                  id="batchNumber"
                  label="Batch Number"
                  onChange={handleChange('batchNumber')}
                  value={values.batchNumber}
                  invalid={touched.batchNumber}
                  error={errors.batchNumber}
                />
                <Input
                  id="productName"
                  label="Product Name"
                  onChange={handleChange('productName')}
                  value={values.productName}
                  invalid={touched.productName}
                  error={errors.productName}
                />
                <FormGroup>
                  <CustomInput
                    type="checkbox"
                    id="printCharts"
                    checked={values.printCharts}
                    label="Print Charts"
                    onChange={(e) =>
                      setFieldValue('printCharts', e.target.checked)
                    }
                  />
                  <CustomInput
                    type="checkbox"
                    id="printNotes"
                    checked={values.printNotes}
                    label="Print Notes"
                    onChange={(e) =>
                      setFieldValue('printNotes', e.target.checked)
                    }
                  />
                  <CustomInput
                    type="checkbox"
                    id="printLimitChanges"
                    checked={values.printLimitChanges}
                    label="Print Limit Changes"
                    onChange={(e) =>
                      setFieldValue('printLimitChanges', e.target.checked)
                    }
                  />
                  <CustomInput
                    type="checkbox"
                    id="printApproval"
                    checked={values.printApproval}
                    label="Print Approval"
                    onChange={(e) =>
                      setFieldValue('printApproval', e.target.checked)
                    }
                  />
                  <CustomInput
                    type="checkbox"
                    id="containsNames"
                    checked={values.containsNames}
                    label="Contains Names"
                    onChange={(e) =>
                      setFieldValue('containsNames', e.target.checked)
                    }
                  />
                </FormGroup>
                <Button
                  id="new-process-btn"
                  classes="mb-15"
                  onClick={(e) => {
                    e.preventDefault();
                    history.push(
                      `/reports/batch-reports/new-process?deptId=${values.deptId}&batchId=${values.id}`,
                    );
                  }}
                >
                  <i className="md-icon">add</i>
                  {t('Add Process')}
                </Button>
                {!values.containsNames && (
                  <Input
                    id="additionalField"
                    label="Additional Field"
                    onChange={(e) => {
                      setFieldValue('additionalField', e.target.value);
                      setAddCol({ ...addCol, text: e.target.value });
                    }}
                    value={values.additionalField}
                    invalid={touched.additionalField}
                    error={errors.additionalField}
                  />
                )}
                <FormGroup style={{ width: '200%' }}>
                  <Label>{t('Processes')}</Label>
                  <Datagrid
                    datagridProps={{
                      keyField: 'id',
                      data: props.processes,
                      loading: props.loading,
                      defaultSorted,
                      columns: [
                        ...columns,
                        values.containsNames ? defaultAddCol : addCol,
                      ],
                    }}
                    ctxMenuOptions={ctxMenuOptions}
                    noSearch
                    noPagination
                    noExport
                  />
                </FormGroup>
                <Input
                  required
                  id="daysSave"
                  label="Days to Save"
                  onChange={handleChange('daysSave')}
                  value={values.daysSave}
                  invalid={touched.daysSave}
                  error={errors.daysSave}
                />
                <Input
                  type="textarea"
                  id="comments"
                  label="Comments"
                  onChange={handleChange('comments')}
                  value={values.comments}
                  invalid={touched.comments}
                  error={errors.comments}
                />
                <Alert.Error text={props.errors} />
                <Button
                  id="create-batch-report"
                  loading={props.isSubmitting}
                  onClick={handleSubmit}
                >
                  <i className="md-icon">done</i>
                  {t('Save')}
                </Button>
              </Form>
            );
          }}
        </Formik>
      </div>
    </Backend>
  );
}

export default withTranslation('app')(EditBatchReportPage);
